import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import {
  getAvdelingerOptions,
  getSeksjonerOptions,
  OwnerId,
  OwnerIdType,
  useAuth,
  useGetAllTeamsQuery,
  useGetAvdelingerQuery,
  useGetOwnerNameQueries,
  useGetRegionerQuery,
  useGetSeksjonerQuery,
  type Avdeling,
  type Seksjon
} from 'prove-common'
import {
  getChildrenAtPath,
  type DelegationNode
} from '../../utils/taskDelegationUtils'
import {
  emptyOrgLookup,
  orgChildren,
  orgUnitName,
  type OrgLookup
} from './orgTree'
import { HIDDEN_DELEGATION_UNITS } from '../../config/hiddenDelegationUnits'
import {
  isDelegationPathHidden,
  visiblePlannedQuantity,
  visibleSamplesTaken
} from '../../utils/delegationVisibility'

export type DelegationLevelItem = {
  ownerId: OwnerId
  name: string
  plannedQuantity: number
  delegatedQuantity: number
  retainedQuantity: number
  /** Antall prøver som allerede er tatt i hele undertreet. */
  samplesTaken: number
  /** Underenhetene i organisasjonen, gruppert på type. Tom for bladnoder. */
  childCounts: Array<{ type: OwnerIdType; count: number }>
  /**
   * Enheten har ingen underenheter, og kan derfor tildeles prøver direkte.
   * Team er alltid bladnoder, men det gjelder også avdelinger og seksjoner
   * som står uten underenheter.
   */
  isLeaf: boolean
  /** An existing delegation stops here even though the unit now has children. */
  isLegacyTarget: boolean
  /**
   * Enheten har en fordeling, men finnes ikke i organisasjonens liste over
   * underenheter. Kan for eksempel være en enhet som er lagt ned.
   */
  isOrphan: boolean
}

export type DelegationLevel = {
  items: DelegationLevelItem[]
  isLoadingChildCounts: boolean
  isLoading: boolean
  isError: boolean
  error?: unknown
  refetch: () => Promise<unknown>
}

const capitalize = (value: string): string =>
  value.replace(/./, (match) => match.toUpperCase())

const countByType = (
  children: OwnerId[]
): Array<{ type: OwnerIdType; count: number }> => {
  const counts = new Map<OwnerIdType, number>()
  for (const child of children) {
    counts.set(child.type, (counts.get(child.type) ?? 0) + 1)
  }
  return [...counts.entries()].map(([type, count]) => ({ type, count }))
}

/**
 * Slår sammen organisasjonsstrukturen med fordelingene som er gjort, slik at vi
 * kan vise ett kort per enhet på nivået brukeren står på – også for enheter som
 * ennå ikke har fått tildelt prøver.
 */
export const useDelegationLevel = (
  delegations: DelegationNode[],
  path: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): DelegationLevel => {
  const { authFetch } = useAuth()
  const parent = path.at(-1)

  const regionId = path[0]?.owner
  const avdelingId = path[1]?.owner

  const regioner = useGetRegionerQuery(parent == null)
  const avdelinger = useGetAvdelingerQuery(
    parent?.type === 'region' ? regionId : undefined
  )
  const seksjoner = useGetSeksjonerQuery(
    parent?.type === 'avdeling' ? avdelingId : undefined
  )
  // Teamene hentes samlet, og foreldrene utledes av `seksjonId`/`avdelingId`.
  // Se orgTree.ts for hvorfor vi ikke bruker seksjonens team-endepunkt.
  const allTeams = useGetAllTeamsQuery(parent != null)

  // Underenhetene til kortene på skjermen, så vi vet hvilke kort som kan åpnes
  // videre og hvor mange underenheter de har.
  const regionIdsOnScreen = useMemo(
    () => (parent == null ? (regioner.data ?? []).map((row) => row.id) : []),
    [parent, regioner.data]
  )
  const avdelingerPerRegion = useQueries({
    queries: regionIdsOnScreen.map((id) => getAvdelingerOptions(authFetch, id)),
    combine: (results) => ({
      data: results.map((result) =>
        Array.isArray(result.data) ? (result.data as Avdeling[]) : []
      ),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
      error: results.find((result) => result.error != null)?.error,
      refetch: () => Promise.all(results.map((result) => result.refetch()))
    })
  })

  const avdelingIdsOnScreen = useMemo(
    () =>
      parent?.type === 'region'
        ? (avdelinger.data ?? []).map((row) => row.id)
        : [],
    [parent, avdelinger.data]
  )
  const seksjonerPerAvdeling = useQueries({
    queries: avdelingIdsOnScreen.map((id) =>
      getSeksjonerOptions(authFetch, id)
    ),
    combine: (results) => ({
      data: results.map((result) =>
        Array.isArray(result.data) ? (result.data as Seksjon[]) : []
      ),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
      error: results.find((result) => result.error != null)?.error,
      refetch: () => Promise.all(results.map((result) => result.refetch()))
    })
  })

  const lookup: OrgLookup = useMemo(() => {
    const next = emptyOrgLookup()
    next.teams = allTeams.data ?? []
    regionIdsOnScreen.forEach((id, index) => {
      next.avdelingerByRegion.set(id, avdelingerPerRegion.data[index] ?? [])
    })
    if (parent?.type === 'region' && regionId != null) {
      next.avdelingerByRegion.set(regionId, avdelinger.data ?? [])
    }
    avdelingIdsOnScreen.forEach((id, index) => {
      next.seksjonerByAvdeling.set(id, seksjonerPerAvdeling.data[index] ?? [])
    })
    if (parent?.type === 'avdeling' && avdelingId != null) {
      next.seksjonerByAvdeling.set(avdelingId, seksjoner.data ?? [])
    }
    return next
  }, [
    allTeams.data,
    regionIdsOnScreen,
    avdelingerPerRegion.data,
    avdelingIdsOnScreen,
    seksjonerPerAvdeling.data,
    avdelinger.data,
    seksjoner.data,
    parent?.type,
    regionId,
    avdelingId
  ])

  const orgUnits = useMemo(() => {
    if (parent == null) {
      return (regioner.data ?? [])
        .map((region) => ({
          ownerId: new OwnerId('region', region.id),
          name: capitalize(region.navn)
        }))
        .filter((unit) => !isDelegationPathHidden([unit.ownerId], hiddenUnits))
    }
    return orgChildren(parent, lookup)
      .filter(
        (ownerId) => !isDelegationPathHidden([...path, ownerId], hiddenUnits)
      )
      .map((ownerId) => ({
        ownerId,
        name: capitalize(orgUnitName(ownerId, lookup) ?? ownerId.owner)
      }))
  }, [parent, regioner.data, lookup, path, hiddenUnits])

  const delegationsAtPath = useMemo(
    () => getChildrenAtPath(delegations, path),
    [delegations, path]
  )

  const orphanOwnerIds = useMemo(
    () =>
      delegationsAtPath
        .filter(
          (delegation) =>
            !isDelegationPathHidden(
              [...path, delegation.ownerId],
              hiddenUnits
            ) &&
            !orgUnits.some(
              (unit) =>
                unit.ownerId.type === delegation.ownerId.type &&
                unit.ownerId.owner === delegation.ownerId.owner
            )
        )
        .map((delegation) => delegation.ownerId),
    [delegationsAtPath, orgUnits, path, hiddenUnits]
  )

  const orphanNames = useGetOwnerNameQueries(
    orphanOwnerIds.map((ownerId) => ownerId.toString())
  )

  const items: DelegationLevelItem[] = useMemo(() => {
    const delegationsByOwner = new Map(
      delegationsAtPath.map((delegation) => [
        delegation.ownerId.toString(),
        delegation
      ])
    )
    const findDelegation = (ownerId: OwnerId) =>
      delegationsByOwner.get(ownerId.toString())
    const quantities = (ownerId: OwnerId, delegation?: DelegationNode) => ({
      plannedQuantity:
        delegation == null
          ? 0
          : visiblePlannedQuantity(delegation, [...path, ownerId], hiddenUnits),
      delegatedQuantity:
        delegation?.children.reduce(
          (sum, child) =>
            sum +
            visiblePlannedQuantity(
              child,
              [...path, ownerId, child.ownerId],
              hiddenUnits
            ),
          0
        ) ?? 0,
      retainedQuantity: delegation?.retainedQuantity ?? 0,
      samplesTaken:
        delegation != null
          ? visibleSamplesTaken(delegation, [...path, ownerId], hiddenUnits)
          : 0
    })

    const fromOrg = orgUnits.map((unit) => {
      const delegation = findDelegation(unit.ownerId)
      const organizationChildren = orgChildren(unit.ownerId, lookup)
      const children = organizationChildren.filter(
        (child) =>
          !isDelegationPathHidden([...path, unit.ownerId, child], hiddenUnits)
      )
      const isLeaf =
        unit.ownerId.type === 'team' ||
        (!avdelingerPerRegion.isLoading &&
          !seksjonerPerAvdeling.isLoading &&
          !allTeams.isLoading &&
          organizationChildren.length === 0)
      return {
        ownerId: unit.ownerId,
        name: unit.name,
        ...quantities(unit.ownerId, delegation),
        childCounts: countByType(children),
        isLeaf,
        isLegacyTarget:
          !isLeaf && delegation != null && delegation.children.length === 0,
        isOrphan: false
      }
    })

    const fromOrphans = orphanOwnerIds.map((ownerId) => {
      const delegation = findDelegation(ownerId)
      const resolved = orphanNames.data.find(
        (owner) => owner.id === ownerId.toString()
      )
      return {
        ownerId,
        name: capitalize(resolved?.name ?? ownerId.owner),
        ...quantities(ownerId, delegation),
        childCounts: [],
        isLeaf: (delegation?.children ?? []).length === 0,
        isLegacyTarget: false,
        isOrphan: true
      }
    })

    return [...fromOrg, ...fromOrphans]
  }, [
    orgUnits,
    delegationsAtPath,
    lookup,
    orphanOwnerIds,
    orphanNames.data,
    avdelingerPerRegion.isLoading,
    seksjonerPerAvdeling.isLoading,
    allTeams.isLoading,
    path,
    hiddenUnits
  ])

  const levelQuery =
    parent == null
      ? regioner
      : parent.type === 'region'
        ? avdelinger
        : parent.type === 'avdeling'
          ? seksjoner
          : allTeams

  return {
    items,
    isLoadingChildCounts:
      avdelingerPerRegion.isLoading ||
      seksjonerPerAvdeling.isLoading ||
      allTeams.isLoading,
    isLoading: levelQuery.isLoading || allTeams.isLoading,
    isError:
      levelQuery.isError ||
      allTeams.isError ||
      avdelingerPerRegion.isError ||
      seksjonerPerAvdeling.isError,
    error:
      levelQuery.error ??
      allTeams.error ??
      avdelingerPerRegion.error ??
      seksjonerPerAvdeling.error,
    refetch: () =>
      Promise.all([
        levelQuery.refetch(),
        allTeams.refetch(),
        avdelingerPerRegion.refetch(),
        seksjonerPerAvdeling.refetch()
      ])
  }
}
